import { useFormApi, useFormState } from 'informed';
import { getMessageWithPlaceholders } from 'shared-locales';
import { isCalendarEventFeedbackAnswerValid } from 'shared-helpers/calendar';
import { useReduxState } from 'hooks';
import { buildCalendarEventFeedbackAnswer } from 'lib/calendar';
import { Button, RichTextValue } from 'components/common';
import {
  Checkbox,
  FormGroup,
  RadioGroup,
  StarsSelector,
  TextArea
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { LocaleMessages } from 'shared-types';
import type { CalendarEventFeedbackQuestion } from 'shared-types';

interface Props {
  disabled?: boolean;
  question: CalendarEventFeedbackQuestion;
  showError?: boolean;
}

const ClearAnswer = ({
  formApi,
  field,
  messages
}: {
  formApi: FormApi<Record<string, unknown>>;
  field: string;
  messages: LocaleMessages;
}) => (
  <div style={{ display: 'block' }}>
    <Button
      category="transparent"
      onClick={() => {
        formApi.setValue(field, '');
        formApi.setValue(`${field}_options`, []);
      }}
      style={{
        backgroundColor: 'transparent',
        padding: 0,
        fontWeight: 'normal'
      }}
      text={messages.commonClear}
      underlineOnHover={true}
    />
  </div>
);

/**
 * Renders a single feedback question with the input matching its type. Every
 * field is named `answer_<questionId>` (multiple choice options add an
 * `_options` array on top) so `CalendarEventFeedbackSubmission` can map the
 * whole form back to answers on submit, keyed by question id.
 *
 * `disabled` covers the whole question, so a form that cannot be submitted
 * cannot be filled in either.
 */
const CalendarEventFeedbackSubmissionQuestion = ({
  disabled = false,
  question,
  showError = false
}: Props) => {
  const { messages, theme } = useReduxState();

  const formApi = useFormApi();
  const { values } = useFormState<Record<string, unknown>>();

  const field = `answer_${question.id}`;

  /**
   * How many options this question expects, phrased for whichever limits it
   * has. An optional question may be skipped entirely, so its limits only
   * become relevant once something has actually been selected - showing them
   * up front would read as if an answer were required.
   */
  const getSelectionCountHelpText = () => {
    if (question.type !== 'multiplechoice') return undefined;

    const {
      numberOfAllowedSelectionsMax: max,
      numberOfAllowedSelectionsMin: min
    } = question.data;

    const helpText = question.isRequired
      ? {
          exact: messages.eventCalendarFeedbackSelectionCountExact,
          max: messages.eventCalendarFeedbackSelectionCountMax,
          range: messages.eventCalendarFeedbackSelectionCountRange
        }
      : {
          exact: messages.eventCalendarFeedbackSelectionCountExactOptional,
          max: messages.eventCalendarFeedbackSelectionCountMaxOptional,
          range: messages.eventCalendarFeedbackSelectionCountRangeOptional
        };

    if (min === max) {
      return getMessageWithPlaceholders({
        message: helpText.exact,
        placeholders: { numberOfOptions: max }
      });
    }

    if (!min) {
      return getMessageWithPlaceholders({
        message: helpText.max,
        placeholders: { numberOfOptions: max }
      });
    }

    return getMessageWithPlaceholders({
      message: helpText.range,
      placeholders: { maximumNumberOfOptions: max, minimumNumberOfOptions: min }
    });
  };

  const getValidationError = () => {
    if (!showError) return undefined;

    const built = buildCalendarEventFeedbackAnswer({ question, values });
    if (!built) {
      return question.isRequired ? messages.commonFieldRequired : undefined;
    }

    if (
      isCalendarEventFeedbackAnswerValid({ answer: built.answer, question })
    ) {
      return undefined;
    }

    // only a choice question can be answered and still be invalid by holding
    // too few selections or not enough - its limits say what is expected
    return getSelectionCountHelpText() || messages.commonFieldRequired;
  };

  const validationError = getValidationError();

  return (
    <div className="question">
      {!question.isRequired ? (
        <div className="question-optional">{messages.commonOptional}</div>
      ) : null}

      <div className="question-text">
        <RichTextValue html={question.question} />
      </div>

      <FormGroup
        error={validationError}
        errorAfterFormElement={true}
        htmlFor={field}
      >
        {!disabled ? (
          <div className="question-help-text">
            {getSelectionCountHelpText()}
          </div>
        ) : null}

        {question.type === 'openquestion' ? (
          <TextArea
            autoGrow={true}
            disabled={disabled}
            field={field}
            id={field}
            maxLength={disabled ? undefined : 2000}
            style={disabled ? { backgroundColor: '#fff' } : {}}
          />
        ) : question.type === 'starrating' ? (
          <>
            <StarsSelector
              disabled={disabled}
              field={field}
              id={field}
              numberOfStars={question.data.numberOfStars}
            />

            {!disabled && !question.isRequired && (
              <ClearAnswer
                formApi={formApi}
                field={field}
                messages={messages}
              />
            )}
          </>
        ) : question.type === 'singlechoice' ? (
          <>
            <RadioGroup
              field={field}
              options={question.data.options.map((option) => ({
                disabled,
                id: `${field}_${option.id}`,
                label: option.text,
                value: option.id
              }))}
            />

            {!disabled && !question.isRequired && (
              <ClearAnswer
                formApi={formApi}
                field={field}
                messages={messages}
              />
            )}
          </>
        ) : (
          (() => {
            const numberOfSelections =
              (values[`${field}_options`] as boolean[])?.filter(Boolean)
                ?.length || 0;

            const selectionLimitReached =
              numberOfSelections >= question.data.numberOfAllowedSelectionsMax;

            return (
              <>
                {question.data.options.map((option, optionIndex) => {
                  const isOptionSelected =
                    values[`${field}_options`]?.[optionIndex] || false;

                  const isOptionDisabled =
                    disabled || (selectionLimitReached && !isOptionSelected);

                  return (
                    <Checkbox
                      disabled={isOptionDisabled}
                      field={`${field}_options[${optionIndex}]`}
                      id={`${field}_${option.id}`}
                      key={option.id}
                      label={option.text}
                      style={{ marginBottom: 5 }}
                    />
                  );
                })}
              </>
            );
          })()
        )}
      </FormGroup>

      <style jsx>{`
        .question {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .question-optional {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          margin-bottom: 10px;
        }
        .question-text {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin-bottom: 15px;
        }
        .question-help-text {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: normal;
          margin-bottom: 5px;
        }
        .clear-answer {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackSubmissionQuestion;
